import React from 'react';
import type { Question, Language, QuestionType } from '@nordcrest/shared';
import {
  SingleSelect,
  MultiSelect,
  Scale,
  Ranking,
  TextInput,
  TextArea,
  Checkbox,
} from '../questions';

interface QuestionRendererProps {
  question: Question;
  value: string | string[] | number | boolean | null;
  onChange: (value: string | string[] | number | boolean | null) => void;
  language: Language;
  error?: string;
}

export function QuestionRenderer({
  question,
  value,
  onChange,
  language,
  error,
}: QuestionRendererProps) {
  const renderQuestion = () => {
    switch (question.type) {
      case 'single_select':
        return (
          <SingleSelect
            question={question}
            value={(value as string) || null}
            onChange={onChange}
            language={language}
            error={error}
          />
        );

      case 'multi_select':
        return (
          <MultiSelect
            question={question}
            value={(value as string[]) || []}
            onChange={onChange}
            language={language}
            error={error}
          />
        );

      case 'scale':
        return (
          <Scale
            question={question}
            value={(value as number) || null}
            onChange={onChange}
            language={language}
            error={error}
          />
        );

      case 'ranking':
        return (
          <Ranking
            question={question}
            value={(value as string[]) || []}
            onChange={onChange}
            language={language}
            error={error}
          />
        );

      case 'text':
      case 'email':
      case 'phone':
        return (
          <TextInput
            question={question}
            value={(value as string) || ''}
            onChange={onChange}
            language={language}
            error={error}
          />
        );

      case 'textarea':
        return (
          <TextArea
            question={question}
            value={(value as string) || ''}
            onChange={onChange}
            language={language}
            error={error}
          />
        );

      case 'checkbox':
        return (
          <Checkbox
            question={question}
            value={(value as boolean) || false}
            onChange={onChange}
            language={language}
            error={error}
          />
        );

      default:
        return <p className="text-red-500">Unknown question type: {question.type}</p>;
    }
  };

  // For checkbox type, the text is shown inline
  if (question.type === 'checkbox') {
    return <div className="py-4">{renderQuestion()}</div>;
  }

  return (
    <div className="py-4">
      <h2 className="text-xl font-semibold text-gray-900 mb-2">
        {question.text[language]}
        {question.required && <span className="text-red-500 ml-1">*</span>}
      </h2>
      {question.description && question.type !== 'scale' && (
        <p className="text-gray-500 mb-4">{question.description[language]}</p>
      )}
      <div className="mt-4">{renderQuestion()}</div>
    </div>
  );
}
